import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  BelowFloorEntry,
  PeriodSaleRefund,
  RefundStockToCheck,
  Sale,
  SalePeriodTotals,
  SaleRefundRow,
} from "../api/types";
import { RefundSaleModal, RefundStockModal } from "../components/sale/RefundSaleModal";
import { SaleBouquetLineDetail } from "../components/sale/SaleBouquetLineDetail";
import { ReasonModal } from "../components/ui/ReasonModal";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { useAuth } from "../state/AuthContext";
import { businessDayKey } from "../utils/businessDay";
import { lineCostIsKnown, saleCostIsKnown, saleCountsAsMoney } from "../utils/salesTotals";
import {
  COST_UNKNOWN,
  figureIfCostKnown,
  joinNotes,
  partlyKnownCost,
  salesWithUnknownCostNote,
} from "../utils/unknownCost";
import { CostNote } from "../components/ui/CostNote";
import { METHOD_LABEL, refundNote, salePaidText } from "../utils/salePayments";
import type { DashboardOutletContext } from "./dashboardOutletContext";

const fmt = (v: string | number | null | undefined) =>
  v === null || v === undefined ? "—" : `€${Number(v).toFixed(2)}`;

/** G-162: "19 Oct" — the day a refund counts on. */
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/**
 * G-162: a sale can be refunded from here when it is not a web order's
 * (refunded in Shopify) and something of it is left to give back — the
 * server's `refundableEur` (zero for an undone sale).
 */
function canRefund(sale: Sale): boolean {
  if (sale.shopifyOrder || sale.customerOrder?.shopifyOrder) return false;
  return Number(sale.refundableEur) > 0;
}

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

type PeriodPreset = "today" | "week" | "month" | "lastMonth" | "custom";

const PERIOD_OPTIONS: Array<{ value: PeriodPreset; label: string }> = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom" },
];

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Whole calendar days, both ends included; the server reads them in Barcelona time. */
function periodRange(
  preset: PeriodPreset,
  customFrom: string,
  customTo: string,
): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "today":
      return { from: ymd(now), to: ymd(now) };
    case "week": {
      const monday = new Date(y, m, now.getDate() - ((now.getDay() + 6) % 7));
      const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
      return { from: ymd(monday), to: ymd(sunday) };
    }
    case "month":
      return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
    case "lastMonth":
      return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case "custom": {
      // A cleared date box must not silently widen the totals to all history.
      const from = customFrom || ymd(now);
      const to = customTo || from;
      return { from, to: to < from ? from : to };
    }
  }
}

function periodLabel({ from, to }: { from: string; to: string }): string {
  const fmtDay = (day: string) =>
    new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  return from === to ? fmtDay(from) : `${fmtDay(from)} – ${fmtDay(to)}`;
}

export function SalesPage() {
  const { user } = useAuth();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const isOwner = user?.role === "OWNER";

  const [sales, setSales] = useState<Sale[]>([]);
  // G-162: refunds GIVEN in the same days — they come off the totals on their own day.
  const [refunds, setRefunds] = useState<PeriodSaleRefund[]>([]);
  // G-169: the period's totals, added once by the server over the same days.
  const [totals, setTotals] = useState<SalePeriodTotals | null>(null);
  // G-162: the refund window, and the owner's removal of a mistaken refund.
  const [refunding, setRefunding] = useState<Sale | null>(null);
  const [removing, setRemoving] = useState<SaleRefundRow | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [filterLocation, setFilterLocation] = useState("");
  const managerLocation = !isOwner ? (user?.assignedLocation ?? null) : null;
  const [preset, setPreset] = useState<PeriodPreset>("month");
  const [customFrom, setCustomFrom] = useState(() => ymd(new Date()));
  const [customTo, setCustomTo] = useState(() => ymd(new Date()));
  const range = useMemo(
    () => periodRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // G-151: the Action log's undo row opens its sale here (`?sale=<id>`). The
  // list waits while the sale is looked up, then loads that sale's Barcelona
  // day (an owner on "All shops"); the row is tinted and scrolled to once, and
  // the parameter leaves the address so nothing re-runs the jump.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSaleId = searchParams.get("sale");
  // G-204: the home page's "Sales today" tile and a shop's name in "By shop"
  // open this page on their days and shop (`?from=YYYY-MM-DD&to=…&location=<id>`).
  // Applied once, then the parameters leave the address like `sale` does.
  const linkedFrom = searchParams.get("from");
  const linkedTo = searchParams.get("to");
  const linkedLocation = searchParams.get("location");
  useEffect(() => {
    if (!linkedFrom && !linkedLocation) return;
    const day = /^\d{4}-\d{2}-\d{2}$/;
    if (linkedFrom && day.test(linkedFrom)) {
      const to = linkedTo && day.test(linkedTo) && linkedTo >= linkedFrom ? linkedTo : linkedFrom;
      if (linkedFrom === ymd(new Date()) && to === linkedFrom) {
        setPreset("today");
      } else {
        setCustomFrom(linkedFrom);
        setCustomTo(to);
        setPreset("custom");
      }
    }
    if (linkedLocation && isOwner) setFilterLocation(linkedLocation);
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("from");
        next.delete("to");
        next.delete("location");
        return next;
      },
      { replace: true },
    );
  }, [linkedFrom, linkedTo, linkedLocation, isOwner, setSearchParams]);
  const [markedSaleId, setMarkedSaleId] = useState<string | null>(null);
  const [jumpPending, setJumpPending] = useState(() => Boolean(linkedSaleId));
  const [jumpError, setJumpError] = useState<string | null>(null);
  // The jump's error survives the load that follows it, not the next one.
  const keepJumpError = useRef(false);
  const pendingScroll = useRef<string | null>(null);
  // Only the newest list request may write the table (a reload started by a
  // filter change, a saved reason or the jump can overtake an older one).
  const requestSeq = useRef(0);
  useEffect(() => {
    if (!linkedSaleId) return;
    let live = true;
    requestSeq.current += 1; // a reload already on its way is now stale
    setJumpPending(true);
    setLoading(true);
    api
      .getSale(linkedSaleId)
      .then((sale) => {
        if (!live) return;
        const day = businessDayKey(new Date(sale.soldAt));
        setCustomFrom(day);
        setCustomTo(day);
        setPreset("custom");
        setFilterLocation("");
        setMarkedSaleId(sale.id);
        setJumpError(null);
        pendingScroll.current = sale.id;
      })
      .catch((err: unknown) => {
        if (!live) return;
        setJumpError(err instanceof Error ? err.message : "Could not open that sale");
        keepJumpError.current = true;
      })
      .finally(() => {
        if (!live) return;
        setJumpPending(false);
        setSearchParams(
          (params) => {
            const next = new URLSearchParams(params);
            next.delete("sale");
            return next;
          },
          { replace: true },
        );
      });
    return () => {
      live = false;
      // Left before the lookup answered (or the jump settled): the list
      // must not wait for it.
      setJumpPending(false);
    };
  }, [linkedSaleId, setSearchParams]);
  // G-45 part 3: below-floor lines still missing their reason. The kiosk
  // florist prompt is the primary door; this is the manager/owner backstop.
  const [belowFloor, setBelowFloor] = useState<BelowFloorEntry[]>([]);
  // G-166 (owner 2026-10-20): Shopify refunds whose stock question is open,
  // and the refund window opened to answer one.
  const [stockToCheck, setStockToCheck] = useState<RefundStockToCheck[]>([]);
  const [checkingStock, setCheckingStock] = useState<{ sale: Sale; refundId: string } | null>(null);
  const [justifying, setJustifying] = useState<BelowFloorEntry | null>(null);
  const [justifyBusy, setJustifyBusy] = useState(false);
  const [justifyError, setJustifyError] = useState<string | null>(null);
  const [justifyNeedsPhoto, setJustifyNeedsPhoto] = useState(false);

  const refresh = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (keepJumpError.current) keepJumpError.current = false;
    else setJumpError(null);
    try {
      const shop = isOwner && filterLocation ? filterLocation : undefined;
      const [list, flagged, given, toCheck, sums] = await Promise.all([
        // G-151 (owner ruling 2026-10-13): undone sales are listed, marked,
        // and left out of every total (the server never counts one).
        api.listSales(shop, range, { includeVoided: true }),
        api.getBelowFloorSales().catch(() => []),
        api.listSaleRefunds(shop, range),
        // G-166: Shopify refunds whose stock nobody checked yet, any day.
        api.listRefundStockToCheck(shop).catch(() => []),
        api.getSalesTotals(shop, range),
      ]);
      if (seq !== requestSeq.current) return;
      setSales(list);
      setRefunds(given);
      setTotals(sums);
      setBelowFloor(flagged);
      setStockToCheck(toCheck);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : "Could not load sales");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [isOwner, filterLocation, range]);

  useEffect(() => {
    if (jumpPending) return;
    void refresh();
  }, [refresh, jumpPending]);

  // Scroll to the linked row once, on the first list loaded after the jump —
  // never on a later reload.
  useEffect(() => {
    const id = pendingScroll.current;
    if (loading || jumpPending || !id) return;
    pendingScroll.current = null;
    document.getElementById(`sale-${id}`)?.scrollIntoView({ block: "center" });
  }, [loading, jumpPending, sales]);

  const closeJustify = () => {
    setJustifying(null);
    setJustifyError(null);
    setJustifyNeedsPhoto(false);
  };

  // The same two-step door the kiosk florist has (BelowFloorPrompt): a
  // bouquet needs a photo before its price can be justified — the server
  // enforces it — so the refusal reveals the camera here instead of failing
  // silently (pre-release fix 2026-09-09).
  const saveReason = async (reason: string | undefined) => {
    if (!justifying || !reason) return;
    setJustifyBusy(true);
    setJustifyError(null);
    try {
      await api.justifyBelowFloorLine(justifying.saleLineId, reason);
      closeJustify();
      await refresh();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Could not save the reason.";
      if (/photo/i.test(message) && justifying.bouquetBuildId) {
        setJustifyNeedsPhoto(true);
        setJustifyError("Add a photo of the bouquet first, then save the reason.");
      } else {
        setJustifyError(message);
      }
    } finally {
      setJustifyBusy(false);
    }
  };

  const attachPhoto = async (file: File) => {
    if (!justifying?.bouquetBuildId) return;
    setJustifyBusy(true);
    setJustifyError(null);
    try {
      await api.addBouquetBuildPhoto(justifying.bouquetBuildId, file);
      setJustifyNeedsPhoto(false);
    } catch (err: unknown) {
      setJustifyError(
        err instanceof Error ? err.message : "Could not upload the photo.",
      );
    } finally {
      setJustifyBusy(false);
    }
  };

  // WO-B (OD-198): COGS and margin leave stock of unknown cost out — say how many sales.
  const unknownCostNote = salesWithUnknownCostNote(totals?.unknownCostSales);
  // G-141 (OD-215): where the period's money was paid, each euro once (the server's columns).
  const paid = totals?.paid ?? null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Sales records</h2>
          <p className="page-subtitle">
            {periodLabel(range)}
          </p>
        </div>
        <Link to="/app/sales/new" className="button">
          Add a sale
        </Link>
      </div>

      {jumpError ? <div className="alert error">{jumpError}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      {stockToCheck.length > 0 ? (
        <div className="card below-floor-prompt">
          <strong>Shopify refunds: stock to check</strong>
          <ul className="forward-demand-list">
            {stockToCheck.map((q) => (
              <li key={q.refundId}>
                {fmtDay(q.refundedAt)} · {q.orderNumber ? `Shopify ${q.orderNumber}` : "Web order"} ·{" "}
                {fmt(q.totalEur)} · {q.location.name}{" "}
                <Link className="button plain" to={`/app/sales?sale=${q.saleId}`}>
                  Open sale
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {belowFloor.length > 0 ? (
        <div className="card below-floor-prompt">
          <strong>Sold below the floor, no reason yet</strong>
          <ul className="forward-demand-list">
            {belowFloor.map((entry) => (
              <li key={entry.saleLineId}>
                {new Date(entry.soldAt).toLocaleDateString()} ·{" "}
                {entry.description} — charged {entry.priceEur} €, floor{" "}
                {entry.floorEur} € · {entry.location.name} · by{" "}
                {entry.soldBy.fullName}{" "}
                <button
                  type="button"
                  className="button plain"
                  onClick={() => setJustifying(entry)}
                >
                  Add reason
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {justifying ? (
        <ReasonModal
          title={`Why was ${justifying.description} sold below the floor?`}
          reasonMode="required"
          reasonPlaceholder="e.g. end of day, stems opening — agreed with the customer"
          confirmLabel="Save reason"
          busy={justifyBusy}
          error={justifyError}
          onConfirm={saveReason}
          onClose={closeJustify}
        >
          {justifyNeedsPhoto ? (
            <div className="field full">
              <label htmlFor="justify-bouquet-photo">Photo of the bouquet</label>
              <input
                id="justify-bouquet-photo"
                type="file"
                accept="image/*"
                capture="environment"
                disabled={justifyBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void attachPhoto(file);
                }}
              />
            </div>
          ) : null}
        </ReasonModal>
      ) : null}

      <div className="card an-filters">
        <div className="field">
          <label htmlFor="filter-location">Shop</label>
          {isOwner ? (
            <select
              id="filter-location"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All shops</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
          ) : (
            <p id="filter-location" className="muted an-note">
              {managerLocation
                ? `${managerLocation.name} (${managerLocation.code}) — your shop`
                : "No shop assigned"}
            </p>
          )}
        </div>
        <div className="field">
          <label id="sales-period-label">Period</label>
          <SegmentedControl
            ariaLabel="Period"
            value={preset}
            onChange={setPreset}
            options={PERIOD_OPTIONS}
          />
        </div>
        {preset === "custom" ? (
          <>
            <div className="field">
              <label htmlFor="sales-from">From</label>
              <input
                id="sales-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="sales-to">To</label>
              <input
                id="sales-to"
                type="date"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </>
        ) : null}
      </div>

      <div className="card" style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap" }}>
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: "uppercase" }}>Revenue</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{fmt(totals?.revenueEur)}</div>
          {totals && refundNote(totals.refundsEur, fmt) ? (
            <div className="muted sales-refunds-note">
              {fmt(totals.soldEur)} sold {refundNote(totals.refundsEur, fmt)}
            </div>
          ) : null}
        </div>
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: "uppercase" }}>Cost of goods</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{fmt(totals?.cogsEur)}</div>
          <CostNote note={unknownCostNote} />
        </div>
        <div>
          {/*
            G-175 (KI-47): the glossary's "Margin" (G-168) — revenue − goods
            IVA − cost of goods, before IVA — worked out by the server
            (`sale-period-totals.ts`, each line's frozen rate). It replaced
            "Margin with IVA" (the stored gross profit), which overstated the
            margin by exactly the goods IVA. The page adds nothing itself.
          */}
          <div className="muted" style={{ fontSize: 12, textTransform: "uppercase" }}>Margin</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{fmt(totals?.marginEur)}</div>
          <CostNote note={joinNotes("before IVA", unknownCostNote)} />
        </div>
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: "uppercase" }}>Tickets</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{totals?.tickets ?? "—"}</div>
        </div>
        <div className="sales-paid-totals" aria-label="How it was paid">
          <SalesStat label="Cash" value={fmt(paid?.cash)} refunded={paid?.refunded.cash} />
          <SalesStat label="Card" value={fmt(paid?.card)} refunded={paid?.refunded.card} />
          <SalesStat label="Online" value={fmt(paid?.online)} refunded={paid?.refunded.online} />
          <SalesStat label="Paid on orders" value={fmt(paid?.onOrders)} refunded={paid?.refunded.onOrders} />
          {paid && Number(paid.notRecorded) > 0 ? <SalesStat label="Not recorded" value={fmt(paid.notRecorded)} /> : null}
        </div>
        <OtherDaysRefunds refunds={refunds} sales={sales} />
      </div>

      <div className="card" style={{ padding: 0, overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Shop</th>
              <th>Sold by</th>
              <th>Items</th>
              <th style={{ textAlign: "right" }}>Revenue</th>
              <th style={{ textAlign: "right" }}>Cost of goods</th>
              <th style={{ textAlign: "right" }}>Margin</th>
              <th>Paid</th>
              <th>
                <span className="visually-hidden">Refund</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: "var(--space-4)" }}>Loading…</td>
              </tr>
            ) : sales.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-state">No sales yet.</td>
              </tr>
            ) : (
              sales.map((s) => (
                <tr
                  key={s.id}
                  id={`sale-${s.id}`}
                  className={[
                    saleCountsAsMoney(s) ? "" : "sales-row-undone",
                    s.id === markedSaleId ? "sales-row-linked" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                >
                  <td>
                    {fmtDate(s.soldAt)}
                    <SaleUndone sale={s} />
                  </td>
                  <td>
                    {s.location.name}{" "}
                    <span className="muted">({s.location.code})</span>
                  </td>
                  <td>
                    {s.soldBy.fullName}
                    <div className="muted" style={{ fontSize: 12 }}>{s.soldBy.role}</div>
                  </td>
                  <td>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {s.lines.map((l) => (
                        <li
                          key={l.id}
                          style={{
                            marginBottom: "var(--space-2)",
                            borderRadius: "var(--radius-md)",
                            background: "var(--color-surface-muted)",
                          }}
                        >
                          {l.kind === "BOUQUET" ? (
                            <>
                              <SaleBouquetLineDetail line={l} />
                              {lineCostIsKnown(l) ? null : (
                                <span className="badge warning sales-line-cost-unknown">{COST_UNKNOWN}</span>
                              )}
                            </>
                          ) : (
                            <div style={{ padding: "var(--space-2)" }}>
                              <span className="badge muted">Item</span>{" "}
                              {Number(l.quantity)} × {l.item?.name ?? "—"}
                              {lineCostIsKnown(l) ? null : (
                                <span className="badge warning sales-line-cost-unknown">{COST_UNKNOWN}</span>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    <SaleRefunds
                      sale={s}
                      onRemove={isOwner ? (r) => { setRemoveError(null); setRemoving(r); } : undefined}
                      onCheckStock={(r) => setCheckingStock({ sale: s, refundId: r.id })}
                    />
                  </td>
                  <td style={{ textAlign: "right" }} className="sales-row-money">{fmt(s.totalEur)}</td>
                  <td style={{ textAlign: "right" }} className="sales-row-money">
                    {partlyKnownCost(s.totalCogsEur, !saleCostIsKnown(s), fmt)}
                  </td>
                  <td style={{ textAlign: "right" }} className="sales-row-money">
                    {figureIfCostKnown(s.marginEur, saleCostIsKnown(s), fmt)}
                  </td>
                  <td className="sales-paid sales-row-money">
                    {salePaidText(s, fmt)}
                    <SuggestedDifference eur={s.suggestedDifferenceEur} />
                  </td>
                  <td className="sales-refund-cell">
                    {canRefund(s) ? (
                      <button type="button" className="button plain" onClick={() => setRefunding(s)}>
                        Refund
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {refunding ? (
        <RefundSaleModal
          sale={refunding}
          onClose={() => setRefunding(null)}
          onRefunded={async () => {
            setRefunding(null);
            await refresh();
          }}
        />
      ) : null}

      {checkingStock ? (
        <RefundStockModal
          sale={checkingStock.sale}
          refundId={checkingStock.refundId}
          onClose={() => setCheckingStock(null)}
          onSaved={async () => {
            setCheckingStock(null);
            await refresh();
          }}
        />
      ) : null}

      {removing ? (
        <ReasonModal
          title={`Remove the refund of ${fmt(removing.totalEur)}?`}
          reasonMode="required"
          confirmLabel="Remove refund"
          destructive
          error={removeError}
          onClose={() => setRemoving(null)}
          onConfirm={async (reason) => {
            if (!reason) return;
            try {
              await api.removeSaleRefund(removing.id, reason);
              setRemoving(null);
              await refresh();
            } catch (err: unknown) {
              setRemoveError(err instanceof Error ? err.message : "Could not remove the refund.");
            }
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * G-151 (owner ruling 2026-10-13): an undone sale stays in the list with an
 * "Undone" pill, who undid it, when and why; it counts in no total.
 */
function SaleUndone({ sale }: { sale: Sale }) {
  if (saleCountsAsMoney(sale)) return null;
  return (
    <div className="sales-undone">
      <span className="badge warning">Undone</span>
      <span className="muted">
        {sale.voidedBy?.fullName ?? "—"}
        {sale.voidedAt ? ` · ${fmtDate(sale.voidedAt)}` : ""}
      </span>
      {sale.voidReason ? <span className="sales-undone-reason">“{sale.voidReason}”</span> : null}
    </div>
  );
}

/**
 * G-162: money given back on this sale — an orange pill per refund, "Refunded
 * €x · 19 Oct · Card" (owner-approved mockup). The sale's own figures stay
 * whole on its own day; the refund comes off the totals of the day it was
 * given. A refund the owner removed reads "Refund removed", struck through:
 * its money came back on the day it was removed. The owner may remove one.
 */
function SaleRefunds({
  sale,
  onRemove,
  onCheckStock,
}: {
  sale: Sale;
  onRemove?: (refund: SaleRefundRow) => void;
  /** G-166: answer a Shopify refund's open stock question. */
  onCheckStock?: (refund: SaleRefundRow) => void;
}) {
  if (!sale.refunds?.length) return null;
  return (
    <div className="sales-refunds">
      {sale.refunds.map((r) => (
        <div key={r.id} className={r.removedAt ? "sales-refund is-removed" : "sales-refund"}>
          <span className="sales-refund-pill">
            {r.removedAt ? "Refund removed" : "Refunded"} {fmt(r.totalEur)} · {fmtDay(r.refundedAt)} ·{" "}
            {r.shopifyRefundId ? "Shopify" : METHOD_LABEL[r.method]}
          </span>
          {r.removedAt ? (
            <span className="muted">
              {r.removedBy?.fullName ?? "—"} · {fmtDay(r.removedAt)}
            </span>
          ) : onRemove && !r.shopifyRefundId ? (
            <button type="button" className="button plain sales-refund-remove" onClick={() => onRemove(r)}>
              Remove
            </button>
          ) : null}
          {r.stockOpen ? (
            onCheckStock ? (
              <button
                type="button"
                className="badge warning sales-stock-check"
                onClick={() => onCheckStock(r)}
              >
                Stock to check
              </button>
            ) : (
              <span className="badge warning">Stock to check</span>
            )
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * G-162: refund events in these days on sales sold on OTHER days — they move
 * the totals above, but their sale is not in the table, so each is named.
 */
function OtherDaysRefunds({ refunds, sales }: { refunds: PeriodSaleRefund[]; sales: Sale[] }) {
  const shown = new Set(sales.map((s) => s.id));
  const others = refunds.filter((r) => !shown.has(r.saleId));
  if (others.length === 0) return null;
  return (
    <div className="sales-other-refunds" aria-label="Refunds of sales from other days">
      {others.map((r) => (
        <span key={r.key} className={r.kind === "REMOVAL" ? "sales-refund-pill is-removal" : "sales-refund-pill"}>
          {r.kind === "REMOVAL" ? "Refund removed" : "Refunded"} {fmt(Math.abs(Number(r.totalEur)))} · {fmtDay(r.at)} ·{" "}
          {r.viaShopify ? "Shopify" : METHOD_LABEL[r.method]} · sale of {fmtDay(r.sale.soldAt)}
        </span>
      ))}
    </div>
  );
}

function SalesStat({ label, value, refunded }: { label: string; value: string; refunded?: string }) {
  const note = refunded === undefined ? null : refundNote(refunded, fmt);
  return (
    <div className="sales-stat">
      <div className="sales-stat-label">{label}</div>
      <div className="sales-stat-value">{value}</div>
      {/* G-162: what refund events did to this column in the period. */}
      {note ? <div className="muted sales-refunds-note">{note}</div> : null}
    </div>
  );
}

/**
 * G-141 (OD-215): under "Paid", how far the price charged was from what the
 * till suggested — "€10.00 under suggested", "€0.50 over suggested", "as
 * suggested"; nothing when nothing was suggested or the sale was priced on an
 * order.
 */
function SuggestedDifference({ eur }: { eur: string | null }) {
  if (eur === null) return null;
  const cents = Math.round(Number(eur) * 100);
  if (cents === 0) return <div className="sales-vs-suggested muted">as suggested</div>;
  return (
    <div className={cents > 0 ? "sales-vs-suggested is-under" : "sales-vs-suggested is-over"}>
      {fmt(Math.abs(cents) / 100)} {cents > 0 ? "under" : "over"} suggested
    </div>
  );
}

